/**
 * LEAVE-ONBEHALF-BAL-001/002 — Apply Leave for Employee (TL/HR/Admin), premium compact dialog.
 * Balances + chargeable days come from the backend (eligible-employees?include_balances=1,
 * on-behalf/chargeable-days). Submission endpoint/payload unchanged (POST /leave/apply-on-behalf).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Paperclip, RefreshCw, UserPlus, X, AlertTriangle, Info, CalendarDays, Send } from 'lucide-react';
import { formatDateOnly } from '../../../utils/formatters';
import { BalanceCards, EmployeePicker, LeaveTypePicker, styleFor, cssVars, fmt } from './OnBehalfPickers';
import './on-behalf-leave.css';

const API = process.env.REACT_APP_BACKEND_URL || '';
const MAX_DOC_BYTES = 1024 * 1024;
const EMPTY_FORM = { employee_user_id: '', leave_type: '', start_date: '', end_date: '', day_part: 'full', reason: '' };
const labelCls = 'block text-[13px] font-medium text-slate-700 mb-1';
const DAY_PARTS = [['full', 'Full Day'], ['half_am', 'Half Day AM'], ['half_pm', 'Half Day PM']];

const Val = ({ v, unit, testId }) => (
  <p className="ob-num" data-testid={testId}><span key={String(v)} className="ob-val-in">{v}{unit && /^[\d.]+$/.test(String(v)) ? <span className="text-xs font-medium text-slate-500"> {Number(v) === 1 ? 'day' : 'days'}</span> : null}</span></p>
);

export const OnBehalfLeaveDialog = ({ open, onClose, onSuccess }) => {
  const [employees, setEmployees] = useState([]);
  const [asOf, setAsOf] = useState(null);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [requested, setRequested] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const calcSeq = useRef(0);
  const fileRef = useRef(null);

  const loadEmployees = async () => {
    setLoadingEmp(true);
    try {
      const res = await axios.get(`${API}/api/leave/on-behalf/eligible-employees`, { params: { include_balances: 1 } });
      setEmployees(res.data?.employees || []);
      setAsOf(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load employees');
    } finally {
      setLoadingEmp(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM); setFile(null); setError(null); setRequested(null); setAsOf(null);
    loadEmployees();
  }, [open]);

  const employee = useMemo(() => employees.find(u => u.id === form.employee_user_id) || null, [employees, form.employee_user_id]);
  const balances = useMemo(() => (employee ? employee.balances || [] : null), [employee]);
  const selectedType = useMemo(() => (balances || []).find(b => b.leave_type_id === form.leave_type) || null, [balances, form.leave_type]);
  const halfDayAllowed = !!selectedType && selectedType.allow_half_day !== false && !!form.start_date && form.start_date === form.end_date;

  useEffect(() => {
    if (!halfDayAllowed && form.day_part !== 'full') setForm(p => ({ ...p, day_part: 'full' }));
  }, [halfDayAllowed, form.day_part]);

  // Authoritative chargeable days (backend calculate_leave_days), debounced 250 ms.
  useEffect(() => {
    if (!form.employee_user_id || !form.start_date || !form.end_date) { setRequested(null); return; }
    const seq = ++calcSeq.current;
    setCalcLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/leave/on-behalf/chargeable-days`, {
          params: { employee_user_id: form.employee_user_id, start_date: form.start_date, end_date: form.end_date, day_part: form.day_part } });
        if (seq === calcSeq.current) setRequested(res.data);
      } catch (err) {
        if (seq === calcSeq.current) setRequested({ error: err.response?.data?.detail || 'Could not calculate days' });
      } finally {
        if (seq === calcSeq.current) setCalcLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [form.employee_user_id, form.start_date, form.end_date, form.day_part]);

  const reqDays = requested && !requested.error ? Number(requested.total_days) : null;
  const isUnpaid = selectedType?.paid === false;
  const available = selectedType && !isUnpaid ? Number(selectedType.available) : null;
  const remaining = available !== null && reqDays !== null ? Math.max(available - reqDays, 0) : null;
  const insufficient = available !== null && reqDays !== null && reqDays > available;
  const lowBalance = !insufficient && available !== null && reqDays !== null && reqDays > 0 && available - reqDays <= 2;
  const noWorkingDays = reqDays === 0;
  const showSummary = !!selectedType && !!form.start_date && !!form.end_date;
  const typeStyle = selectedType ? styleFor(selectedType) : null;
  const canSubmit = !!employee && !!selectedType && !!form.start_date && !!form.end_date && form.reason.trim().length >= 5 && !insufficient && !submitting;
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > MAX_DOC_BYTES) {
      setFile(null); if (fileRef.current) fileRef.current.value = '';
      setError(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)} MB — supporting documents must be 1 MB or smaller.`);
      return;
    }
    setError(null); setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setSubmitting(true); setError(null);
      const res = await axios.post(`${API}/api/leave/apply-on-behalf`, form);
      let docWarning = '';
      if (file && res.data?.id) {
        try {
          const fd = new FormData(); fd.append('file', file);
          await axios.post(`${API}/api/leave-registry/requests/${res.data.id}/documents`, fd);
        } catch (docErr) {
          console.error('Document upload failed', docErr);
          docWarning = ' Note: the supporting document failed to upload — please attach it again from the request.';
        }
      }
      onSuccess?.({ employeeName: employee?.full_name || 'employee', docWarning });
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit leave';
      setError(msg);
      toast.error(`Leave submission failed: ${msg}`, { duration: 7000 });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const note = requested?.error ? ['err', requested.error]
    : insufficient ? ['err', `Insufficient ${selectedType.leave_type_name} balance for ${employee.full_name}: requested ${fmt(reqDays)}, available ${fmt(available)}.`]
      : noWorkingDays ? ['warn', 'The selected range contains no working days (weekends/company holidays only).']
        : lowBalance ? ['warn', `Low balance: ${fmt(remaining)} day(s) will remain after this request.`]
          : isUnpaid && showSummary ? ['info', 'Unpaid leave — no balance is deducted.']
            : requested?.holidays_in_range?.length > 0 ? ['info', `Includes ${requested.holidays_in_range.length} company holiday(s) within the range (sandwich policy).`]
              : null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[60] p-4" data-testid="on-behalf-modal">
      <div className="ob-modal ob-fade bg-white w-full max-w-[520px] max-h-[94vh] overflow-y-auto">
        <div className="flex items-center gap-3 px-5 pt-4 pb-3 border-b border-slate-100">
          <span className="ob-icon-badge h-9 w-9 rounded-xl flex items-center justify-center"><UserPlus className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /></span>
          <h3 className="text-[17px] font-semibold text-slate-900 flex-1">Apply Leave for Employee</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100" aria-label="Close" data-testid="on-behalf-close-btn"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-3.5">
          <div>
            <label className={labelCls}>Employee *</label>
            <EmployeePicker employees={employees} value={form.employee_user_id} loading={loadingEmp}
              onChange={(id) => setForm(p => ({ ...p, employee_user_id: id, leave_type: '', day_part: 'full' }))} />
          </div>

          <div className="ob-panel" data-testid="on-behalf-balance-strip" data-state={balances ? 'loaded' : 'empty'}>
            <div className="flex items-center gap-2 mb-2">
              <span className="h-7 w-7 rounded-lg bg-white border border-slate-200 text-blue-600 flex items-center justify-center"><CalendarDays className="w-3.5 h-3.5" /></span>
              <span className="text-sm font-semibold text-slate-800">Leave Balance</span>
              <span className="ml-auto text-xs text-slate-500" data-testid="on-behalf-balance-asof">As of {balances && asOf ? formatDateOnly(asOf) : '--'}</span>
              <button type="button" onClick={loadEmployees} disabled={loadingEmp || !balances} className="p-1 rounded text-blue-600 hover:bg-white disabled:opacity-40" aria-label="Refresh balances" data-testid="on-behalf-balance-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${loadingEmp ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div key={employee?.id || 'empty'} className={balances ? 'ob-fade' : ''}>
              <BalanceCards balances={balances} selectedId={form.leave_type} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Leave Type *</label>
            <LeaveTypePicker balances={balances} value={form.leave_type} disabled={!employee}
              onChange={(id) => setForm(p => ({ ...p, leave_type: id, day_part: 'full' }))} />
          </div>

          {showSummary && (
            <div className="ob-summary ob-fade" style={cssVars(typeStyle)} data-testid="on-behalf-summary">
              <div className="flex items-center gap-2 min-w-0 pr-2">
                <span className="ob-type-tile" style={cssVars(typeStyle)}><typeStyle.Icon className="w-3.5 h-3.5" /></span>
                <span className="text-[12.5px] font-semibold text-slate-800 leading-tight whitespace-nowrap">{selectedType.leave_type_name}{selectedType.abbreviation ? ` (${selectedType.abbreviation})` : ''}</span>
              </div>
              <div className="ob-cell"><p className="ob-lbl">Available</p><Val v={isUnpaid ? 'Unlimited' : fmt(available)} unit testId="on-behalf-summary-available" /></div>
              <div className="ob-cell" data-loading={calcLoading}><p className="ob-lbl">Requested</p><Val v={requested?.error ? '—' : calcLoading && reqDays === null ? '…' : fmt(reqDays)} unit testId="on-behalf-summary-requested" /></div>
              <div className="ob-cell"><p className="ob-lbl">Remaining</p><p className={`ob-num ${insufficient ? 'text-red-600' : ''}`} data-testid="on-behalf-summary-remaining"><span key={String(remaining)} className="ob-val-in">{isUnpaid ? '—' : fmt(remaining)}{!isUnpaid && remaining !== null ? <span className="text-xs font-medium text-slate-500"> {Number(remaining) === 1 ? 'day' : 'days'}</span> : null}</span></p></div>
            </div>
          )}
          {note && (
            <p className={`ob-fade -mt-1.5 flex items-start gap-1.5 text-xs ${note[0] === 'err' ? 'text-red-700' : note[0] === 'warn' ? 'text-amber-700' : 'text-slate-600'}`} data-testid="on-behalf-balance-warning">
              {note[0] === 'info' ? <Info className="w-3.5 h-3.5 mt-px shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />}<span>{note[1]}</span>
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Start Date *</label>
              <input type="date" value={form.start_date} required disabled={!employee} data-testid="on-behalf-start-date" className="ob-input"
                onChange={(e) => setForm(p => ({ ...p, start_date: e.target.value, end_date: p.end_date && p.end_date < e.target.value ? e.target.value : p.end_date }))} />
            </div>
            <div>
              <label className={labelCls}>End Date *</label>
              <input type="date" value={form.end_date} required disabled={!employee} min={form.start_date || undefined} data-testid="on-behalf-end-date" className="ob-input" onChange={set('end_date')} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Day Part{selectedType && selectedType.allow_half_day === false ? <span className="ml-1.5 text-[11px] font-normal text-slate-400">· half-day not permitted for {selectedType.abbreviation || selectedType.leave_type_name}</span> : null}</label>
            <div className={`ob-seg ${!employee ? 'is-disabled' : ''}`} role="radiogroup" aria-label="Day part" data-testid="on-behalf-day-part" data-half-enabled={halfDayAllowed}>
              {DAY_PARTS.map(([v, l]) => (
                <button key={v} type="button" role="radio" aria-checked={form.day_part === v} disabled={!employee || (v !== 'full' && !halfDayAllowed)}
                  onClick={() => setForm(p => ({ ...p, day_part: v }))} data-testid={`on-behalf-day-part-${v}`}>
                  <span className="ob-radio" />{l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Reason *</label>
            <textarea value={form.reason} required minLength={5} maxLength={500} rows={2} className="ob-input" disabled={!employee}
              placeholder="Add reason for leave..." data-testid="on-behalf-reason-input" onChange={set('reason')} />
          </div>
          <div>
            <label className={`${labelCls} flex items-center gap-1.5`}>
              <Paperclip className="w-3.5 h-3.5 text-slate-500" /> Supporting document (optional, max 1 MB)
              <span className="ml-auto text-blue-500" title="PDF, image or document up to 1 MB. Attached after the request is created." data-testid="on-behalf-file-info"><Info className="w-4 h-4" /></span>
            </label>
            <input ref={fileRef} type="file" data-testid="on-behalf-file-input" onChange={pickFile} disabled={!employee}
              className="w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-slate-300 file:bg-white file:text-sm file:text-slate-700 disabled:opacity-60" />
          </div>

          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="on-behalf-error">{error}</div>}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={submitting} data-testid="on-behalf-cancel-btn" className="ob-btn-secondary flex-1 px-4 py-2.5 text-sm disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={!canSubmit} data-testid="on-behalf-submit-btn" className="ob-btn-primary flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit for Employee</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnBehalfLeaveDialog;
